import React, { useState } from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import classNames from 'classnames'
import { getAvatarThumbUrl, parseAvatarSize } from '@paintscout/util'
import type { StyleClasses } from '../../styles'
import Avatar from '../Avatar/Avatar'
import type { AvatarProps } from '../Avatar/Avatar'
import Tooltip from '../Tooltip'

const useStyles = makeStyles<Theme, EstimatorAvatarProps>(
  () => ({
    img: {
      borderRadius: '50%',
      objectFit: 'cover',
      height: ({ height }) => `${height || '28px'} !important`,
      width: ({ width }) => `${width || '28px'} !important`,
      marginRight: '0px !important'
    },
    avatar: {
      padding: '0px !important',
      boxShadow: ({ borderColor }) => (borderColor ? `0 0 0 2px white, 0 0 0 4px ${borderColor} !important` : undefined)
    }
  }),
  { name: 'EstimatorAvatar' }
)

export interface EstimatorAvatarUser {
  id?: string
  firstName?: string
  lastName?: string
}

export interface EstimatorAvatarProps extends AvatarProps {
  classes?: StyleClasses<typeof useStyles>
  userPictures: {
    [key: string]: string
  }
  user?: EstimatorAvatarUser
  borderColor?: string
  hideTooltip?: boolean
  /**
   * Whether the underlying user is still active. When false, the avatar keeps the user's colour
   * and is drawn at half opacity — the look Settings → User Management uses (ENG-2184). An explicit
   * `style.opacity` still wins. The tooltip is appended with " (deactivated)".
   */
  active?: boolean
  /**
   * How to render when no user is supplied (or `user.id` is missing).
   * - `'placeholder'` (default): legacy behavior — renders "PaintScout User" initials.
   * - `'blank'`: renders nothing (`null`). Use when the owner was intentionally
   *   removed (e.g. via the "Remove estimator" deactivation flow).
   */
  renderEmpty?: 'placeholder' | 'blank'
}

function addAuth0Prefix(id: string) {
  if (!id) return id
  return id.includes('auth0|') ? id : `auth0|${id}`
}

/**
 * Avatars render tiny (28px by default) but `customAvatar.cdnValue` points at the full-resolution
 * upload. On a list with many avatars (e.g. every card on the Deals board) that's potentially hundreds
 * of multi-MB images decoded into memory at once — a real OOM driver on low-memory iPads (#21020).
 *
 * `getAvatarThumbUrl` is shared with the other avatar renderers (#22459). This component pins
 * `dpr: 2` rather than reading the device's, so its delivered URLs — and the warm CDN cache entries
 * behind them — stay byte-identical to what has shipped since #21034 across all 30+ call sites.
 */
const AVATAR_DPR = 2

const INACTIVE_OPACITY = 0.5

export default function EstimatorAvatar(props: EstimatorAvatarProps) {
  const {
    userPictures,
    user,
    borderColor,
    hideTooltip,
    active = true,
    renderEmpty = 'placeholder',
    ...avatarProps
  } = props
  const classes = useStyles(props)
  const [imgLoading, setImgLoading] = useState(false)

  if (!user?.id && renderEmpty === 'blank') {
    return null
  }

  const userPicture = user?.id ? userPictures?.[addAuth0Prefix(user.id)] : null
  const avatarSize = parseAvatarSize([props.width, props.height])
  // At 20px or smaller there's no room for two initials — render a single character instead.
  const oneLetter = [props.width, props.height].some((value) => {
    const parsed = Number.parseInt(String(value ?? ''), 10)
    return parsed > 0 && parsed <= 20
  })
  const thumbSrc = userPicture ? getAvatarThumbUrl(userPicture, avatarSize, { dpr: AVATAR_DPR }) : userPicture
  const userName = user?.id
    ? `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'PaintScout User'
    : 'PaintScout User'

  const avatarColor = borderColor ?? props.avatarColor
  const tooltipContent = active ? userName : `${userName} (deactivated)`

  // The initials variant gets consumer styles via `{...avatarProps}`; the image variant used to
  // silently drop them, so `style={{ opacity: 0.5 }}` greyed out initials but not photos (ENG-1048).
  const styleProp = (avatarProps as { style?: React.CSSProperties }).style
  const targetOpacity = styleProp?.opacity ?? (active ? 1 : INACTIVE_OPACITY)

  const Picture = userPicture ? (
    <img
      onClick={props.onClick}
      className={classNames(classes.img, 'disable-click')}
      src={thumbSrc}
      // defer fetch/decode of off-screen avatars (e.g. long Deals board) to avoid loading hundreds
      // of images into memory at once — a key OOM driver on low-memory iPads (#21020)
      loading="lazy"
      decoding="async"
      style={{
        ...styleProp,
        // keep the fade-in gate authoritative over any consumer opacity
        opacity: imgLoading ? 0 : targetOpacity,
        transition: 'opacity 0.2s ease-in',
        boxShadow: borderColor
          ? `0 0 0 2px white, 0 0 0 4px ${borderColor}` // White outline followed by colored outline
          : undefined,
        filter: styleProp?.filter
      }}
      alt={userName}
      onLoad={() => {
        setImgLoading(false)
      }}
    />
  ) : (
    <Avatar
      className={classNames(classes.avatar, 'disable-click')}
      backgroundColor="#FFFFFF"
      avatarColor={avatarColor}
      disableTooltip
      filled
      noTransform
      width="28px"
      height="28px"
      fontSize={'12px'}
      {...avatarProps}
      style={{ ...styleProp, opacity: targetOpacity }}
      oneLetter={oneLetter}
    >
      {/* Limit the username to two words so we only get two initials (one when the avatar is tiny) */}
      {userName
        .toUpperCase()
        .split(/\s+/)
        .slice(0, oneLetter ? 1 : 2)
        .join(' ')}
    </Avatar>
  )

  if (hideTooltip) {
    return Picture
  }
  return (
    <Tooltip hideIcon content={tooltipContent} enterDelay={500} enterNextDelay={500}>
      {Picture}
    </Tooltip>
  )
}
